<script setup lang="ts">
import CollapsibleSearchPanel from '@/components/Globals/CollapsibleSearchPanel.vue'
import Loader from '@/components/Globals/Feedbacks/Loader.vue'
import PageHeader from '@/components/Globals/PageHeader.vue'
import DailyTemperatureGrid from '@/components/DailyTemperature/DailyTemperatureGrid.vue'
import DailyTemperatureGridFooter from '@/components/DailyTemperature/DailyTemperatureGridFooter.vue'
import DailyTemperatureGridToolbar from '@/components/DailyTemperature/DailyTemperatureGridToolbar.vue'
import DailyTemperaturePdfModal from '@/components/DailyTemperature/DailyTemperaturePdfModal.vue'
import DailyTemperatureSearchForm from '@/components/DailyTemperature/DailyTemperatureSearchForm.vue'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import { useDailyTemperatureSearch } from '@/composables/useDailyTemperatureSearch'
import { SensorType } from '@/interfaces/enums'
import { useCompanyStore } from '@/store/company'
import { useSensorStore } from '@/store/sensor'
import { resetShiftIfUnavailable } from '@/utils/daily-temperature/period-filter'
import { useI18n } from '@/composable/useI18n'
import { ChartBarIcon } from '@heroicons/vue/24/outline'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'

const { t } = useI18n()

const companyStore = useCompanyStore()
const sensorStore = useSensorStore()
const { shifts, hasShifts } = storeToRefs(companyStore)
const { listSensors } = storeToRefs(sensorStore)

const {
  draftPrimaryDate,
  draftCompareDate,
  draftIsCompareEnabled,
  draftPeriodFilter,
  draftSensorIds,
  isCompareGridActive,
  hasSearched,
  isLoading,
  hours,
  appliedPeriodFilterLabel,
  tableSensors,
  primarySensorById,
  compareSensorById,
  primaryDateShort,
  compareDateShort,
  hasGridData,
  isEmptyResult,
  hasMore,
  isLoadingMore,
  loadedSensorsCount,
  totalSensors,
  remainingSensors,
  clearFilters,
  search,
  loadMore,
  maxDate,
} = useDailyTemperatureSearch()

const showPdfModal = ref(false)
const pdfSeedSensorIds = ref<number[]>([])

const temperatureSensors = computed(() =>
  listSensors.value.filter((sensor) => sensor.sensor_type === SensorType.TEMPERATURE),
)

function openPdfModal() {
  pdfSeedSensorIds.value = [...draftSensorIds.value]
  showPdfModal.value = true
}

function closePdfModal() {
  showPdfModal.value = false
}

watch(
  () => companyStore.currentCompany?.id,
  (companyId) => {
    if (companyId) {
      companyStore.fetchCompanyShifts()
      sensorStore.fetch()
    }
  },
  { immediate: true },
)

watch(hasShifts, (nextHasShifts) => {
  draftPeriodFilter.value = resetShiftIfUnavailable(draftPeriodFilter.value, nextHasShifts)
})
</script>

<template>
  <DefaultLayout>
    <div class="mx-auto min-w-0 w-full max-w-[1600px] px-4 py-6 lg:px-8">
      <PageHeader
        :to="{ name: 'dashboard' }"
        :title="t('daily_temperature.title')"
        :subtitle="t('daily_temperature.subtitle')">
        <template #actions>
          <button
            type="button"
            class="flex items-center gap-2 rounded-none bg-blue px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-2"
            @click="openPdfModal">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {{ t('daily_temperature.buttons.generate_pdf') }}
          </button>
        </template>
      </PageHeader>

      <CollapsibleSearchPanel :title="t('daily_temperature.panel.title')">
        <DailyTemperatureSearchForm
          v-model:primary-date="draftPrimaryDate"
          v-model:compare-date="draftCompareDate"
          v-model:is-compare-enabled="draftIsCompareEnabled"
          v-model:period-filter="draftPeriodFilter"
          v-model:sensor-ids="draftSensorIds"
          :temperature-sensors="temperatureSensors"
          :shifts="shifts"
          :has-shifts="hasShifts"
          :max-date="maxDate"
          :is-loading="isLoading"
          @clear-filters="clearFilters"
          @search="search" />
      </CollapsibleSearchPanel>

      <div class="mt-4">
        <div
          v-if="!hasSearched"
          class="flex min-h-[280px] flex-col items-center justify-center border border-dashed border-stroke bg-gray-2 px-6 py-12 text-center">
          <ChartBarIcon class="mb-4 h-14 w-14 text-gray-300" aria-hidden="true" />
          <p class="font-satoshi text-lg font-bold text-black">
            {{ t('daily_temperature.empty.no_selection') }}
          </p>
          <p class="mt-2 max-w-md text-sm font-medium text-gray-500">
            {{ t('daily_temperature.empty.no_selection_hint') }}
          </p>
        </div>

        <Loader v-else-if="isLoading" />

        <div
          v-else-if="isEmptyResult"
          class="flex flex-col items-center justify-center bg-white px-6 py-12 shadow-sm">
          <p class="text-lg font-medium text-gray-900">
            {{ t('daily_temperature.empty.no_sensors') }}
          </p>
          <p class="mt-2 text-sm text-gray-500">
            {{ t('daily_temperature.empty.no_sensors_hint') }}
          </p>
        </div>

        <template v-else-if="hasGridData">
          <DailyTemperatureGridToolbar :period-label="appliedPeriodFilterLabel" />
          <DailyTemperatureGrid
            :hours="hours"
            :table-sensors="tableSensors"
            :is-compare-enabled="isCompareGridActive"
            :primary-date-short="primaryDateShort"
            :compare-date-short="compareDateShort"
            :primary-sensor-by-id="primarySensorById"
            :compare-sensor-by-id="compareSensorById" />
          <DailyTemperatureGridFooter
            :has-more="hasMore"
            :is-loading-more="isLoadingMore"
            :loaded-count="loadedSensorsCount"
            :total-count="totalSensors"
            :remaining-count="remainingSensors"
            @load-more="loadMore" />
        </template>
      </div>

      <DailyTemperaturePdfModal
        :is-open="showPdfModal"
        :default-primary-date="draftPrimaryDate"
        :default-sensor-ids="pdfSeedSensorIds"
        :temperature-sensors="temperatureSensors"
        :shifts="shifts"
        :has-shifts="hasShifts"
        @close="closePdfModal" />
    </div>
  </DefaultLayout>
</template>
